import React from "react";
import { useNavigate } from "react-router-dom";
import { dashboardAPI } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  AlertTriangle, 
  Clock, 
  Users, 
  CheckCircle2,
  AlertCircle,
  Calendar,
  ExternalLink,
  RefreshCw,
  Target,
  Activity,
  TrendingUp
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import PredictiveAnalytics from "@/components/PredictiveAnalytics";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch (e) {
    return dateStr;
  }
}

function KPICard(props) {
  var clickable = props.onClick ? "cursor-pointer hover:bg-muted/50 transition-colors" : "";
  return (
    <Card className={clickable} onClick={props.onClick} data-testid={props.testId}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{props.label}</p>
            <p className="text-2xl font-bold">{props.value}</p>
            {props.subtitle && <p className="text-xs text-muted-foreground mt-1">{props.subtitle}</p>}
          </div>
          <div className="p-3 rounded-full bg-primary/10">{props.icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProjectAtRiskItem(props) {
  var p = props.project;
  var nav = props.navigate;
  var levelClass = p.risk_level === "red" ? "bg-red-500" : "bg-amber-500";
  return (
    <div className="p-3 border rounded-lg hover:bg-muted/50">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{p.name}</span>
            <Badge className={levelClass}>{p.risk_level === "red" ? "Critical" : "At Risk"}</Badge>
          </div>
          <p className="text-sm text-muted-foreground truncate">{p.customer}</p>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span>OSR: {p.osr}</span>
            <span>Due: {formatDate(p.end_date)}</span>
          </div>
          <p className="text-xs text-red-600 mt-1">{p.risk_reasons.join(", ")}</p>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold">{(p.completion || 0).toFixed(0)}%</div>
          <Progress value={p.completion || 0} className="w-16 h-2 mt-1" />
        </div>
      </div>
      <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={function() { nav("/project/" + p.id); }}>
        <ExternalLink className="h-3 w-3 mr-1" /> View
      </Button>
    </div>
  );
}

function DeadlineItem(props) {
  var p = props.project;
  var isUrgent = p.days_until <= 7;
  var urgentClass = isUrgent ? "border-amber-300 bg-amber-50/50" : "";
  var badgeVariant = isUrgent ? "destructive" : "secondary";
  var daysText = p.days_until === 0 ? "Today" : p.days_until === 1 ? "Tomorrow" : p.days_until + " days";
  return (
    <div className={"p-3 border rounded-lg " + urgentClass}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{p.name}</p>
          <p className="text-xs text-muted-foreground">OSR: {p.osr}</p>
        </div>
        <div className="text-right">
          <Badge variant={badgeVariant}>{daysText}</Badge>
          <p className="text-xs text-muted-foreground mt-1">{(p.completion || 0).toFixed(0)}% done</p>
        </div>
      </div>
    </div>
  );
}

function TaskItem(props) {
  var t = props.task;
  var nav = props.navigate;
  var overdueClass = t.is_overdue ? "border-red-300 bg-red-50/50" : "";
  var badgeVariant = t.is_overdue ? "destructive" : t.days_until <= 3 ? "secondary" : "outline";
  var daysText = t.is_overdue ? Math.abs(t.days_until) + "d late" : t.days_until === 0 ? "Today" : t.days_until + "d";
  
  function handleClick() {
    if (t.project_id && nav) {
      nav("/project/" + t.project_id);
    }
  }
  
  return (
    <div 
      className={"p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors " + overdueClass}
      onClick={handleClick}
      data-testid={"task-item-" + t.id}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{t.name}</p>
          <p className="text-xs text-muted-foreground truncate mt-1">{t.project}</p>
        </div>
        <Badge variant={badgeVariant} className="shrink-0">{daysText}</Badge>
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-muted-foreground">Assigned: <span className="font-medium text-foreground">{t.assigned_to || "Unassigned"}</span></span>
        <span className="text-muted-foreground">Due: {formatDate(t.due_date)}</span>
      </div>
      <div className="flex items-center justify-between mt-2">
        <Badge variant="outline" className="text-xs">{t.status}</Badge>
        <span className="text-xs text-primary flex items-center gap-1">
          <ExternalLink className="h-3 w-3" /> View Project
        </span>
      </div>
    </div>
  );
}

function WorkloadChart(props) {
  var data = props.data || [];
  var color = props.color || "#6366f1";
  var onBarClick = props.onBarClick;
  
  if (data.length === 0) {
    return <p className="text-muted-foreground text-center py-4">No data available</p>;
  }
  
  var chartData = data.slice(0, 8);
  
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        {chartData.map(function(entry, index) {
          var maxTasks = Math.max(...chartData.map(function(e) { return e.tasks; }));
          var width = (entry.tasks / maxTasks) * 100;
          
          return (
            <div 
              key={index} 
              className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded transition-colors"
              onClick={function() { if (onBarClick) onBarClick(entry); }}
            >
              <span className="text-xs text-muted-foreground w-24 truncate text-right">{entry.name}</span>
              <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                <div 
                  className="h-full rounded transition-all duration-300"
                  style={{ width: width + "%", backgroundColor: color }}
                />
              </div>
              <span className="text-xs font-medium w-8 text-right">{entry.tasks}</span>
            </div>
          );
        })}
      </div>
      {onBarClick && <p className="text-xs text-muted-foreground text-center">Click a row to view tasks</p>}
    </div>
  );
}

export default function Dashboard() {
  var navigate = useNavigate();
  var { user } = useAuth();
  var dataState = React.useState(null);
  var data = dataState[0];
  var setData = dataState[1];
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  // State for workload task dialog
  var taskDialogState = React.useState(false);
  var showTaskDialog = taskDialogState[0];
  var setShowTaskDialog = taskDialogState[1];
  var selectedPersonState = React.useState(null);
  var selectedPerson = selectedPersonState[0];
  var setSelectedPerson = selectedPersonState[1];
  
  // State for unassigned tasks dialog
  var unassignedDialogState = React.useState(false);
  var showUnassignedDialog = unassignedDialogState[0];
  var setShowUnassignedDialog = unassignedDialogState[1];

  // State for task completion dialog
  var taskCompletionDialogState = React.useState(false);
  var showTaskCompletionDialog = taskCompletionDialogState[0];
  var setShowTaskCompletionDialog = taskCompletionDialogState[1];

  // State for project progress dialog
  var progressDialogState = React.useState(false);
  var showProgressDialog = progressDialogState[0];
  var setShowProgressDialog = progressDialogState[1];

  // State for project health filter dialog
  var healthDialogState = React.useState(false);
  var showHealthDialog = healthDialogState[0];
  var setShowHealthDialog = healthDialogState[1];
  var healthFilterState = React.useState("all");
  var healthFilter = healthFilterState[0];
  var setHealthFilter = healthFilterState[1];

  // State for project status dialog
  var statusDialogState = React.useState(false);
  var showStatusDialog = statusDialogState[0];
  var setShowStatusDialog = statusDialogState[1];
  var statusFilterState = React.useState("all");
  var statusFilter = statusFilterState[0];
  var setStatusFilter = statusFilterState[1];

  function fetchData() {
    setLoading(true);
    // If user is not management, filter by their resource_id
    var resourceId = null;
    if (user && !user.is_management && user.resource_id) {
      resourceId = user.resource_id;
    }
    
    dashboardAPI.getExecutive(resourceId)
      .then(function(res) { setData(res.data.data); })
      .catch(function(err) { console.error("Failed:", err); })
      .finally(function() { setLoading(false); });
  }
  
  function handleWorkloadBarClick(entry) {
    if (entry && entry.tasks > 0) {
      setSelectedPerson(entry);
      setShowTaskDialog(true);
    }
  }
  
  function goToActiveProjects() {
    navigate("/projects?status=Active");
  }

  React.useEffect(function() { fetchData(); }, [user]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  var kpis = data?.kpis || {};
  var atRisk = data?.projects_at_risk || { items: [], summary: {} };
  var deadlines = data?.upcoming_deadlines || { projects: [], tasks: [] };
  var workload = data?.resource_workload || { osr: [], isr: [], unassigned_tasks: 0 };
  var statusCounts = data?.project_status_counts || {};
  var allProjects = data?.all_projects || [];

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Executive Dashboard</h1>
          <p className="text-muted-foreground">Project health and performance overview</p>
        </div>
        <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
          <RefreshCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
        </Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="predictive" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Predictive Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard 
          label="Active Projects" 
          value={kpis.active_projects || 0} 
          subtitle={"of " + kpis.total_projects + " total"} 
          icon={<Target className="h-5 w-5 text-primary" />} 
          onClick={goToActiveProjects}
          testId="active-projects-kpi"
        />
        <KPICard 
          label="Task Completion" 
          value={(kpis.task_completion_rate || 0) + "%"} 
          subtitle={kpis.completed_tasks + " of " + kpis.total_tasks + " active tasks"} 
          icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
          onClick={function() { setShowTaskCompletionDialog(true); }}
          testId="task-completion-kpi"
        />
        <KPICard 
          label="Avg Progress" 
          value={(kpis.avg_completion || 0).toFixed(0) + "%"} 
          subtitle="across active projects" 
          icon={<Activity className="h-5 w-5 text-blue-600" />}
          onClick={function() { setShowProgressDialog(true); }}
          testId="avg-progress-kpi"
        />
        <KPICard 
          label="Unassigned Tasks" 
          value={workload.unassigned_tasks || 0} 
          subtitle="in active projects" 
          icon={<AlertCircle className="h-5 w-5 text-amber-600" />}
          onClick={workload.unassigned_tasks > 0 ? function() { setShowUnassignedDialog(true); } : undefined}
          testId="unassigned-tasks-kpi"
        />
      </div>

      <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={function() { setShowStatusDialog(true); }}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2"><Target className="h-5 w-5" />Project Status Overview</CardTitle>
          <CardDescription>Click to view projects by status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-xl font-bold">{statusCounts.Active || 0}</span>
              <span className="text-sm text-muted-foreground">Active</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-xl font-bold">{statusCounts["Pre Project Planning"] || 0}</span>
              <span className="text-sm text-muted-foreground">Pre-Planning</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-xl font-bold">{statusCounts.Completed || 0}</span>
              <span className="text-sm text-muted-foreground">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-xl font-bold">{statusCounts["On Hold"] || 0}</span>
              <span className="text-sm text-muted-foreground">On Hold</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-500"></div>
              <span className="text-xl font-bold">{statusCounts.Archived || 0}</span>
              <span className="text-sm text-muted-foreground">Archived</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-xl font-bold">{statusCounts.Canceled || 0}</span>
              <span className="text-sm text-muted-foreground">Canceled</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" />Projects At Risk</CardTitle>
            <CardDescription>Projects behind schedule or at risk</CardDescription>
          </CardHeader>
          <CardContent>
            {atRisk.items.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                <p>All projects on track!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {atRisk.items.slice(0, 5).map(function(p) {
                  return <ProjectAtRiskItem key={p.id} project={p} navigate={navigate} />;
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-blue-500" />Upcoming Deadlines</CardTitle>
            <CardDescription>Projects due in 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            {deadlines.projects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No upcoming deadlines</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deadlines.projects.slice(0, 6).map(function(p) {
                  return <DeadlineItem key={p.id} project={p} />;
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-indigo-500" />OSR Workload</CardTitle>
            <CardDescription>Tasks per Outside Sales Rep</CardDescription>
          </CardHeader>
          <CardContent>
            <WorkloadChart data={workload.osr} color="#6366f1" onBarClick={handleWorkloadBarClick} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-teal-500" />ISR Workload</CardTitle>
            <CardDescription>Tasks per Inside Sales Rep</CardDescription>
          </CardHeader>
          <CardContent>
            <WorkloadChart data={workload.isr} color="#14b8a6" onBarClick={handleWorkloadBarClick} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-orange-500" />Critical Tasks</CardTitle>
          <CardDescription>Tasks due or overdue</CardDescription>
        </CardHeader>
        <CardContent>
          {deadlines.tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p>No critical tasks</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {deadlines.tasks.slice(0, 9).map(function(t) {
                return <TaskItem key={t.id} task={t} navigate={navigate} />;
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Task Dialog for Workload */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPerson?.name}&apos;s Tasks</DialogTitle>
            <DialogDescription>
              {selectedPerson?.tasks} active tasks assigned
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {selectedPerson?.task_details && selectedPerson.task_details.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedPerson.task_details.map(function(task, idx) {
                    var isOverdue = task.days_until < 0;
                    var isDueSoon = task.days_until >= 0 && task.days_until <= 7;
                    return (
                      <TableRow key={idx} className={isOverdue ? "bg-red-50" : isDueSoon ? "bg-amber-50" : ""}>
                        <TableCell className="font-medium">{task.name}</TableCell>
                        <TableCell>{task.project}</TableCell>
                        <TableCell>
                          <Badge variant={task.status === "In Progress" ? "default" : "secondary"}>
                            {task.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={isOverdue ? "text-red-600 font-medium" : isDueSoon ? "text-amber-600" : ""}>
                            {formatDate(task.due_date)}
                            {isOverdue && " (overdue)"}
                            {isDueSoon && !isOverdue && " (soon)"}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">No task details available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Unassigned Tasks Dialog */}
      <Dialog open={showUnassignedDialog} onOpenChange={setShowUnassignedDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Unassigned Tasks</DialogTitle>
            <DialogDescription>
              {workload.unassigned_tasks || 0} tasks in active projects without an assigned resource
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {workload.unassigned_task_details && workload.unassigned_task_details.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workload.unassigned_task_details.map(function(task, idx) {
                    var isOverdue = task.days_until < 0;
                    var isDueSoon = task.days_until >= 0 && task.days_until <= 7;
                    return (
                      <TableRow key={idx} className={isOverdue ? "bg-red-50" : isDueSoon ? "bg-amber-50" : ""}>
                        <TableCell className="font-medium">{task.name}</TableCell>
                        <TableCell>{task.project}</TableCell>
                        <TableCell>{task.role || "-"}</TableCell>
                        <TableCell>
                          <span className={isOverdue ? "text-red-600 font-medium" : isDueSoon ? "text-amber-600" : ""}>
                            {formatDate(task.due_date)}
                            {isOverdue && " (overdue)"}
                            {isDueSoon && !isOverdue && " (soon)"}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">No unassigned tasks</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Completion Dialog */}
      <Dialog open={showTaskCompletionDialog} onOpenChange={setShowTaskCompletionDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Task Completion Breakdown
            </DialogTitle>
            <DialogDescription>
              Task status distribution across active projects
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border bg-green-50">
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold text-green-600">{kpis.completed_tasks || 0}</p>
              </div>
              <div className="p-4 rounded-lg border bg-amber-50">
                <p className="text-sm text-muted-foreground">Remaining</p>
                <p className="text-3xl font-bold text-amber-600">{(kpis.total_tasks || 0) - (kpis.completed_tasks || 0)}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Completion Rate</span>
                <span className="font-medium">{kpis.task_completion_rate || 0}%</span>
              </div>
              <Progress value={kpis.task_completion_rate || 0} className="h-3" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Progress Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Project Progress Overview
            </DialogTitle>
            <DialogDescription>
              Average completion: {(kpis.avg_completion || 0).toFixed(0)}% across {kpis.active_projects || 0} active projects
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-3">
            {atRisk.items && atRisk.items.length > 0 ? (
              atRisk.items.map(function(p, idx) {
                return (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.customer}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={p.completion || 0} className="w-24 h-2" />
                      <span className="text-sm font-medium w-12 text-right">{(p.completion || 0).toFixed(0)}%</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-muted-foreground py-8">No project data available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Health Dialog */}
      <Dialog open={showHealthDialog} onOpenChange={setShowHealthDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Project Health Details
            </DialogTitle>
            <DialogDescription>
              Filter by health status to see project details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant={healthFilter === "all" ? "default" : "outline"} size="sm" onClick={function() { setHealthFilter("all"); }}>
                All ({(atRisk.summary.critical || 0) + (atRisk.summary.warning || 0) + (atRisk.summary.healthy || 0)})
              </Button>
              <Button variant={healthFilter === "critical" ? "default" : "outline"} size="sm" onClick={function() { setHealthFilter("critical"); }} className="text-red-600">
                Critical ({atRisk.summary.critical || 0})
              </Button>
              <Button variant={healthFilter === "warning" ? "default" : "outline"} size="sm" onClick={function() { setHealthFilter("warning"); }} className="text-amber-600">
                At Risk ({atRisk.summary.warning || 0})
              </Button>
              <Button variant={healthFilter === "healthy" ? "default" : "outline"} size="sm" onClick={function() { setHealthFilter("healthy"); }} className="text-green-600">
                Healthy ({atRisk.summary.healthy || 0})
              </Button>
            </div>
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {atRisk.items && atRisk.items.filter(function(p) {
                if (healthFilter === "all") return true;
                if (healthFilter === "critical") return p.risk_level === "red";
                if (healthFilter === "warning") return p.risk_level === "yellow";
                if (healthFilter === "healthy") return !p.risk_level || p.risk_level === "green";
                return true;
              }).map(function(p, idx) {
                var levelClass = p.risk_level === "red" ? "border-l-red-500" : p.risk_level === "yellow" ? "border-l-amber-500" : "border-l-green-500";
                return (
                  <div key={idx} className={"p-3 rounded-lg border border-l-4 " + levelClass}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.customer} • OSR: {p.osr}</p>
                        {p.risk_reasons && p.risk_reasons.length > 0 && (
                          <p className="text-xs text-red-600 mt-1">{p.risk_reasons.join(", ")}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">{(p.completion || 0).toFixed(0)}%</p>
                        <p className="text-xs text-muted-foreground">Due: {formatDate(p.end_date)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Projects by Status
            </DialogTitle>
            <DialogDescription>
              Click a status to filter and view projects
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button variant={statusFilter === "all" ? "default" : "outline"} size="sm" onClick={function() { setStatusFilter("all"); }}>
                All ({Object.values(statusCounts).reduce(function(a, b) { return a + b; }, 0)})
              </Button>
              <Button variant={statusFilter === "Active" ? "default" : "outline"} size="sm" onClick={function() { setStatusFilter("Active"); }} className="text-green-600">
                Active ({statusCounts.Active || 0})
              </Button>
              <Button variant={statusFilter === "Pre Project Planning" ? "default" : "outline"} size="sm" onClick={function() { setStatusFilter("Pre Project Planning"); }} className="text-blue-600">
                Pre-Planning ({statusCounts["Pre Project Planning"] || 0})
              </Button>
              <Button variant={statusFilter === "On Hold" ? "default" : "outline"} size="sm" onClick={function() { setStatusFilter("On Hold"); }} className="text-amber-600">
                On Hold ({statusCounts["On Hold"] || 0})
              </Button>
              <Button variant={statusFilter === "Completed" ? "default" : "outline"} size="sm" onClick={function() { setStatusFilter("Completed"); }} className="text-purple-600">
                Completed ({statusCounts.Completed || 0})
              </Button>
              <Button variant={statusFilter === "Archived" ? "default" : "outline"} size="sm" onClick={function() { setStatusFilter("Archived"); }} className="text-gray-600">
                Archived ({statusCounts.Archived || 0})
              </Button>
              <Button variant={statusFilter === "Canceled" ? "default" : "outline"} size="sm" onClick={function() { setStatusFilter("Canceled"); }} className="text-red-600">
                Canceled ({statusCounts.Canceled || 0})
              </Button>
            </div>
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {allProjects && allProjects.filter(function(p) {
                if (statusFilter === "all") return true;
                return p.status === statusFilter;
              }).length > 0 ? (
                allProjects.filter(function(p) {
                  if (statusFilter === "all") return true;
                  return p.status === statusFilter;
                }).map(function(p, idx) {
                  var statusColor = {
                    "Active": "border-l-green-500",
                    "Pre Project Planning": "border-l-blue-500",
                    "On Hold": "border-l-amber-500",
                    "Completed": "border-l-purple-500",
                    "Archived": "border-l-gray-500",
                    "Canceled": "border-l-red-500"
                  }[p.status] || "border-l-gray-300";
                  return (
                    <div key={idx} className={"p-3 rounded-lg border border-l-4 cursor-pointer hover:bg-muted/50 " + statusColor} onClick={function() { navigate("/project/" + p.id); }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{p.name}</p>
                          <p className="text-xs text-muted-foreground">{p.customer} • OSR: {p.osr}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary">{p.status}</Badge>
                          <p className="text-xs text-muted-foreground mt-1">{(p.completion || 0).toFixed(0)}% complete</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-muted-foreground py-8">No projects with this status</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="predictive">
          <PredictiveAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
}
